import React, { useEffect, useState } from 'react';

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';

import { IconArrowLeft, IconChevronDown, IconRefresh } from '@tabler/icons';

import { useHistory, useParams } from 'react-router-dom';

import axios from 'axios';

const hostname = window.location.hostname;
let AGENT_SERVER;

if (hostname.startsWith('crm.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostname.startsWith('glazzbeauty.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostname.startsWith('casamathiola.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostname.startsWith('bliveinpets.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostname.startsWith('acin.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostname.startsWith('braza55.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostname.startsWith('po.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostname.startsWith('florestavet.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostname.startsWith('quimicer.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostname.startsWith('imobiliaria.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else {
    AGENT_SERVER = 'http://127.0.0.1:7000';
}

const agentApi = axios.create({
    baseURL: AGENT_SERVER
});

agentApi.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

const STATUS_COLOR = {
    completed: 'success',
    running: 'info',
    timeout: 'warning',
    cancelled: 'warning',
    error: 'error'
};

const formatDate = (iso) => {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleString('pt-BR');
    } catch (e) {
        return iso;
    }
};

const formatMs = (ms) => (typeof ms === 'number' ? `${ms.toFixed(0)}ms` : '-');
const formatPercent = (value) => (typeof value === 'number' ? `${(value * 100).toFixed(0)}%` : '-');

const FINDING_COLOR = { info: 'default', warning: 'warning', error: 'error' };

const EvaluationBadge = ({ evaluation }) => {
    if (!evaluation) return <Chip size="small" variant="outlined" label="não avaliado" />;
    if (evaluation.passed === null || evaluation.passed === undefined) {
        return (
            <Chip
                size="small"
                variant="outlined"
                label={typeof evaluation.overall_score === 'number' ? evaluation.overall_score.toFixed(2) : 'avaliado'}
            />
        );
    }
    return (
        <Chip
            size="small"
            color={evaluation.passed ? 'success' : 'error'}
            label={
                typeof evaluation.overall_score === 'number'
                    ? `${evaluation.passed ? 'ok' : 'falhou'} · ${evaluation.overall_score.toFixed(2)}`
                    : evaluation.passed
                      ? 'ok'
                      : 'falhou'
            }
        />
    );
};

const SummaryStat = ({ label, value }) => (
    <Box sx={{ textAlign: 'center', px: 2, py: 1.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {value}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {label}
        </Typography>
    </Box>
);

const EvaluationSummaryDashboard = ({ summary }) => {
    if (!summary) return null;

    return (
        <Card sx={{ borderRadius: '20px', mb: 3 }}>
            <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                    Diagnóstico geral (últimas {summary.total_executions} execuções)
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={4} md={2}>
                        <SummaryStat label="Execuções" value={summary.total_executions} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <SummaryStat label="Taxa de sucesso" value={formatPercent(summary.success_rate)} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <SummaryStat
                            label="Score médio"
                            value={typeof summary.average_score === 'number' ? summary.average_score.toFixed(2) : '-'}
                        />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <SummaryStat label="Taxa de erro" value={formatPercent(summary.error_rate)} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <SummaryStat label="Taxa de loops" value={formatPercent(summary.loop_rate)} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <SummaryStat label="Transferência humana" value={formatPercent(summary.human_handoff_rate)} />
                    </Grid>
                </Grid>

                {summary.top_tools && summary.top_tools.length > 0 && (
                    <>
                        <Divider sx={{ mb: 1.5 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Tools mais usadas
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            {summary.top_tools.map((tool) => (
                                <Chip key={tool.tool_name} size="small" variant="outlined" label={`${tool.tool_name} (${tool.count})`} />
                            ))}
                        </Box>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

const StepActions = ({ actions }) => {
    if (!actions || actions.length === 0) {
        return (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Nenhuma ação nesse step.
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {actions.map((action, idx) => (
                <Box
                    key={idx}
                    sx={{
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: '10px',
                        p: 1.5
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                        <Chip size="small" label={action.action} />
                        {action.tool_name && <Chip size="small" variant="outlined" label={action.tool_name} />}
                        {action.verdict && (
                            <Chip
                                size="small"
                                label={action.verdict}
                                color={action.verdict === 'DENY' ? 'error' : action.verdict === 'ALLOW' ? 'success' : 'warning'}
                            />
                        )}
                    </Box>

                    {action.parameters && Object.keys(action.parameters).length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                parâmetros
                            </Typography>
                            <Box component="pre" sx={{ m: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {JSON.stringify(action.parameters, null, 2)}
                            </Box>
                        </Box>
                    )}

                    {action.result && (
                        <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                resultado
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {action.result}
                            </Typography>
                        </Box>
                    )}

                    {action.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {action.error}
                        </Alert>
                    )}
                </Box>
            ))}
        </Box>
    );
};

const EvaluationPanel = ({ evaluation }) => {
    if (!evaluation) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                Esta execução ainda não foi avaliada.
            </Alert>
        );
    }

    const metrics = Object.values(evaluation.metrics || {});

    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Avaliação
                </Typography>
                <EvaluationBadge evaluation={evaluation} />
            </Box>

            {metrics.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    {metrics.map((metric) => (
                        <Chip
                            key={metric.name}
                            size="small"
                            variant="outlined"
                            color={metric.passed === false ? 'error' : metric.passed === true ? 'success' : 'default'}
                            label={`${metric.name}: ${metric.value === null || metric.value === undefined ? 'n/d' : metric.value}`}
                        />
                    ))}
                </Box>
            )}

            {(evaluation.findings || []).length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {evaluation.findings.map((finding, idx) => (
                        <Alert
                            key={idx}
                            severity={FINDING_COLOR[finding.severity] === 'default' ? 'info' : finding.severity}
                            sx={{ py: 0 }}
                        >
                            {finding.step_number ? `[step ${finding.step_number}] ` : ''}
                            {finding.message || finding.code}
                        </Alert>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const TraceDetail = ({ trace }) => {
    if (!trace) return null;

    return (
        <Box>
            {trace.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {trace.error}
                </Alert>
            )}

            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Mensagem: <strong>{trace.message || '-'}</strong>
            </Typography>

            <EvaluationPanel evaluation={trace.evaluation} />
            <Divider sx={{ mb: 2 }} />

            {(trace.steps || []).map((step) => (
                <Accordion key={step.step_number} defaultExpanded={trace.steps.length === 1}>
                    <AccordionSummary expandIcon={<IconChevronDown size={18} />}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip size="small" label={`step ${step.step_number}`} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {step.decision}
                            </Typography>
                            {step.model && <Chip size="small" variant="outlined" label={step.model} />}
                            {typeof step.total_tokens === 'number' && (
                                <Chip size="small" variant="outlined" label={`${step.total_tokens} tokens`} />
                            )}
                            {typeof step.latency_ms === 'number' && (
                                <Chip size="small" variant="outlined" label={formatMs(step.latency_ms)} />
                            )}
                            {step.loop_signal && <Chip size="small" color="warning" label={`loop: ${step.loop_signal}`} />}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {step.loop_reason && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {step.loop_reason}
                            </Alert>
                        )}
                        <StepActions actions={step.actions} />
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

const AgentTraces = () => {
    const history = useHistory();
    const { agentId } = useParams();

    const [traces, setTraces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [summary, setSummary] = useState(null);

    const [selectedId, setSelectedId] = useState(null);
    const [selectedTrace, setSelectedTrace] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const fetchTraces = async () => {
        setLoading(true);
        setError('');
        try {
            const [tracesRes, summaryRes] = await Promise.all([
                agentApi.get(`/agent/api/v1/agent/${agentId}/traces/`),
                agentApi.get(`/agent/api/v1/agent/${agentId}/evaluations/summary/`)
            ]);
            setTraces(tracesRes.data.traces || []);
            setSummary(summaryRes.data.summary || null);
        } catch (err) {
            setError('Não foi possível carregar as execuções deste agente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTraces();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId]);

    const handleSelect = async (traceId) => {
        setSelectedId(traceId);
        setSelectedTrace(null);
        setLoadingDetail(true);
        try {
            const res = await agentApi.get(`/agent/api/v1/agent/${agentId}/traces/${traceId}/`);
            setSelectedTrace(res.data.trace);
        } catch (err) {
            setError('Não foi possível carregar o detalhe dessa execução.');
        } finally {
            setLoadingDetail(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Button startIcon={<IconArrowLeft size={18} />} onClick={() => history.goBack()} sx={{ mb: 2, textTransform: 'none' }}>
                        Voltar
                    </Button>

                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            background: 'linear-gradient(90deg, #1976d2, #9c27b0)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Execuções do Agente
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Diagnóstico das últimas execuções: contexto, decisão, tool e resultado de cada step.
                    </Typography>
                </Box>

                <Button startIcon={<IconRefresh size={18} />} onClick={fetchTraces} sx={{ textTransform: 'none' }}>
                    Atualizar
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                    {error}
                </Alert>
            )}

            <EvaluationSummaryDashboard summary={summary} />

            <Card sx={{ borderRadius: '20px', mb: 3 }}>
                <CardContent sx={{ p: 0 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : traces.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Nenhuma execução registrada ainda para este agente.
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Mensagem</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Avaliação</TableCell>
                                        <TableCell>Modelo</TableCell>
                                        <TableCell>Tokens</TableCell>
                                        <TableCell>Latência</TableCell>
                                        <TableCell>Quando</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {traces.map((trace) => (
                                        <TableRow
                                            key={trace.id}
                                            hover
                                            selected={selectedId === trace.id}
                                            onClick={() => handleSelect(trace.id)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell
                                                sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                            >
                                                {trace.message || <em>(sem mensagem)</em>}
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" label={trace.status} color={STATUS_COLOR[trace.status] || 'default'} />
                                            </TableCell>
                                            <TableCell>
                                                <EvaluationBadge evaluation={trace.evaluation} />
                                            </TableCell>
                                            <TableCell>{trace.model || '-'}</TableCell>
                                            <TableCell>{trace.total_tokens ?? '-'}</TableCell>
                                            <TableCell>{formatMs(trace.latency_ms)}</TableCell>
                                            <TableCell>{formatDate(trace.started_at)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {selectedId && (
                <Card sx={{ borderRadius: '20px' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            Detalhe da execução
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {loadingDetail ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <TraceDetail trace={selectedTrace} />
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default AgentTraces;
